<script lang="ts">
import GaiaStatusIndicator, { type GaiaIndicatorStatus } from '../../base/GaiaStatusIndicator.vue';

/** One pipeline step on the card — its own label, the status from a statusMaps adapter. */
export interface GaiaIntakeStep {
  label: string;
  status: GaiaIndicatorStatus;
  glyph?: 'dot' | 'check';
}
</script>

<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaText from '../../base/GaiaText.vue';
import { gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  title: {
    type: String,
    default: 'An agent is working on this file',
  },
  steps: {
    type: Array as PropType<GaiaIntakeStep[]>,
    required: true,
  },
  hint: {
    type: String,
    default: "Results appear here as soon as it's sorted.",
  },
  /** The hint is the card's message (something is stuck), set like a step rather than a footnote. */
  prominentHint: {
    type: Boolean,
    default: false,
  },
  /** In a field column beside the preview rather than centred in the pane. */
  compact: {
    type: Boolean,
    default: false,
  },
});

const surface = gaiaNeutral.surface;
const cardBorder = gaiaNeutral.borderCard;
</script>

<template>
  <!-- The detail pane while a file is still on its way into a contract
       (Contracts Overview mock, "Running"): a small card centred in the pane,
       one row per pipeline step. Compact, it heads the field column once the
       preview has taken the pane. -->
  <div class="gaia-intake-progress" :class="{ compact: props.compact }">
    <div class="card">
      <gaia-text tag="div" size="row" weight="semibold" tone="ink">{{ props.title }}</gaia-text>
      <div v-for="step in props.steps" :key="step.label" class="step">
        <gaia-status-indicator :status="step.status" :glyph="step.glyph" />
        <gaia-text size="dense" :tone="step.status === 'pending' ? 'subtle' : 'ink'">
          {{ step.label }}
        </gaia-text>
      </div>
      <gaia-text
        v-if="props.hint"
        tag="div"
        :size="props.prominentHint ? 'dense' : 'nano'"
        :tone="props.prominentHint ? 'ink' : 'subtle'"
      >
        {{ props.hint }}
      </gaia-text>
    </div>
  </div>
</template>

<style scoped>
.gaia-intake-progress {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.gaia-intake-progress.compact {
  flex: none;
  padding: 0;
}

.card {
  width: 340px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px;
  background: v-bind(surface);
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
}

.compact .card {
  width: 100%;
}

.step {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
